import { useEffect } from 'react';
import { applyPageMeta } from '../lib/seo';
import { PAGE_META } from '../lib/routeMeta';

/**
 * Privacy Notice for the Gamerhood S5 site (a vendor-operated marketing
 * activation site), written as the site-specific authority. Wording is the
 * legal-reviewed 2026-06-26 copy, plus the 2026-07-06 State Farm Privacy
 * Policy reference in Section 1 requested by State Farm legal (GHS5-73).
 *
 * VERSIONING: PRIVACY_VERSION is displayed at the top of the page. Unlike
 * TERMS_VERSION it has no database consent record; bump it when the notice
 * changes materially. LAST_UPDATED is the human-readable date; keep them in
 * sync.
 */
export const PRIVACY_VERSION = '2026-v4';
export const PRIVACY_LAST_UPDATED = '2026-07-06';

const PRIVACY_CONTACT = 'privacy@infinitymarketing.com';

export default function PrivacyPage() {
  useEffect(() => {
    applyPageMeta(PAGE_META.privacy);
  }, []);

  return (
    <article className="mx-auto max-w-3xl px-6 py-16 text-white/85">
      <header className="border-b border-white/15 pb-6">
        <h1 className="font-display text-3xl uppercase tracking-widest text-brand-redAccent">
          Privacy Notice
        </h1>
        <p className="mt-3 text-sm text-white/60">
          Version {PRIVACY_VERSION} &middot; Last updated {PRIVACY_LAST_UPDATED}
        </p>
      </header>

      <RulesAuthorityNotice />

      <div className="mt-8 space-y-12 leading-relaxed">
        <Section id="who-we-are" title="1. Who we are">
          <p>
            This website (statefarmgamerhood.com and its related staging
            subdomains) is operated by{' '}
            <strong>Infinity Marketing Team, LLC</strong> ("Infinity Marketing,"
            "we," "us") on behalf of{' '}
            <strong>State Farm Mutual Automobile Insurance Company</strong>{' '}
            ("State Farm"). The site exists to support the Gamerhood Season 5
            contest and the related game,{' '}
            <em>Jake's Neighborhood Swarm</em>.
          </p>
          <p>
            This notice describes how this site collects and uses personal
            information. For the personal information processed through this
            site, Infinity Marketing acts as a service provider/processor on
            State Farm's behalf for contest-related processing, and as an
            independent controller for site-operations data (analytics,
            security, error logs) used to run and improve the site. State Farm
            is the controller for contest participation data, marketing
            communications you opt into, and prize fulfillment. Personal
            information collected by State Farm for these purposes is subject
            to State Farm's{' '}
            <a
              href="https://www.statefarm.com/customer-care/privacy-security/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-redAccent underline-offset-2 hover:underline"
            >
              Privacy Policy
            </a>
            .
          </p>
        </Section>

        <Section id="what-we-collect" title="2. Collection of Personal Information">
          <p>
            We may collect the following categories of personal information:
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              <strong>Identifiers and contact information.</strong> Your first
              name, last name, email address, country of residence, and
              (optionally, if you provide one) your phone number — collected
              when you register to play.
            </li>
            <li>
              <strong>Authentication data.</strong> A one-time passcode sent to
              your email, and short-lived session and refresh tokens stored
              in cookies on your device after you verify.
            </li>
            <li>
              <strong>Contest participation data.</strong> Your
              acknowledgment of the Official Rules each week you play, the
              version of the rules you agreed to, your confirmation that you
              meet the age requirement, and whether you opted into marketing
              communications from State Farm.
            </li>
            <li>
              <strong>Gameplay data.</strong> Your scores, game session
              metadata, in-run pick selections, coin balances, and aggregate
              performance signals (e.g., average frame rate). Submission from
              the game is end-to-end encrypted between the game and our server
              using a per-session key.
            </li>
            <li>
              <strong>Device and connection information.</strong> Your IP
              address (used for security, rate limiting, and approximate
              country detection via CloudFront), browser user agent, and the
              pages and features you interact with on the site.
            </li>
            <li>
              <strong>Approximate location (country only).</strong> Derived
              from the network-level signal CloudFront provides, used to
              determine contest eligibility (U.S. residents only) and to
              show the appropriate consent surface. We do not collect
              precise GPS location.
            </li>
            <li>
              <strong>Your privacy choices.</strong> The record of the
              cookie-consent decision you made, the version of the consent
              notice you saw, and the timestamp of your decision — stored in
              your browser and, optionally, on our servers as an audit
              record under{' '}
              <a href="#legal-bases" className="underline">
                Article 7(1) GDPR
              </a>
              .
            </li>
          </ul>
        </Section>

        <Section id="how-we-use-it" title="3. Use of Personal Information">
          <p>We may use your personal information to:</p>
          <ul className="ml-6 list-disc space-y-2">
            <li>verify your identity and keep you signed in;</li>
            <li>
              run the contest — process your entries, confirm eligibility,
              record consent to the Official Rules, and (if you win) notify you
              and fulfill prizes;
            </li>
            <li>
              operate the game — issue per-session game tokens, accept
              encrypted score submissions, maintain the leaderboard, and
              credit in-game coin balances;
            </li>
            <li>
              measure how the site is used so we can improve it — through
              cookieless analytics (Plausible), which runs in all regions, and
              Google Analytics via Google Tag Manager, which is gated by
              region: on by default in the United States and other non-GDPR
              regions (you can opt out any time) and only after you opt in for
              the EEA, UK, and Switzerland (see Section 4). For visitors in
              the United States, we also read your Analytics preference from
              the State Farm Do Not Sell or Share preference center (via a
              cross-domain cookie set by State Farm) and use it to set our
              Google Analytics state. If you have made an explicit choice on
              our Privacy Settings page, that choice takes precedence;
            </li>
            <li>
              support State Farm marketing measurement and attribution through
              the DEEL tag — loaded <strong>only for visitors in the United
              States</strong>. Visitors outside the U.S. do not have the DEEL
              script loaded at all, regardless of consent. U.S. visitors can
              still opt out at any time on the Privacy Settings page;
            </li>
            <li>
              send the email communications you opt into (contest
              reminders, marketing) and the operational emails you cannot opt
              out of (one-time passcodes, security notices);
            </li>
            <li>
              protect the site and our users — detect fraud and abuse,
              enforce the rules, defend against attacks;
            </li>
            <li>
              comply with applicable laws and respond to lawful requests.
            </li>
          </ul>
        </Section>

        <Section id="legal-bases" title="4. How tracking is gated by region — and our legal bases">
          <p>
            How non-essential cookies and similar technologies are gated
            depends on where you are:
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              <strong>United States (except California) and other non-GDPR
              regions.</strong>{' '}
              Analytics storage (Google Analytics via Google Tag Manager) and
              advertising/marketing storage (State Farm's DEEL tag) are{' '}
              <strong>on by default</strong>. U.S. state privacy laws operate
              on an opt-out basis; we rely on that basis together with our
              legitimate interest in measuring and supporting the activation.
              You can opt out at any time on the{' '}
              <a href="/privacy-settings" className="underline">
                Privacy Settings
              </a>{' '}
              page, through the "Do Not Sell or Share" link in our footer, or
              by sending a Global Privacy Control signal from your browser.
            </li>
            <li>
              <strong>California.</strong> Nothing non-essential runs until you
              give affirmative consent. Visitors from these regions see a
              consent banner on their first visit; analytics and
              marketing-storage state stay off unless and until you accept, and
              you can change your choice at any time.
            </li>
            <li>
              <strong>EEA, UK, and Switzerland.</strong> Nothing non-essential
              runs until you give affirmative consent. Visitors from these
              regions see a consent banner on their first visit; analytics and
              marketing-storage state stay off unless and until you accept,
              and you can change your choice at any time. Note that the State
              Farm DEEL advertising tag is <strong>not loaded for any visitor
              outside the United States</strong> regardless of consent — see
              Section 5.
            </li>
            <li>
              <strong>Everywhere.</strong> Strictly necessary storage (your
              sign-in session, the record of your consent choice, a short-lived
              country cache) always runs — it is what makes the site work. Our
              cookieless analytics (Plausible) also runs by default in all
              regions because it stores nothing on your device; you can still
              opt out of it on the Privacy Settings page.
            </li>
          </ul>
          <p>
            If you are in the European Economic Area, the United Kingdom, or
            Switzerland, we rely on the following legal bases under GDPR
            Article 6 (and UK GDPR / Swiss revFADP equivalents):
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              <strong>Performance of a contract</strong> — Art. 6(1)(b) — for
              the parts of our processing necessary to give you the account
              and play experience you signed up for.
            </li>
            <li>
              <strong>Your consent</strong> — Art. 6(1)(a) — for non-essential
              cookies and similar technologies (analytics storage, marketing
              storage) and for marketing communications. You can withdraw
              consent at any time on the{' '}
              <a href="/privacy-settings" className="underline">
                Privacy Settings
              </a>{' '}
              page or by clicking the unsubscribe link in any marketing email.
            </li>
            <li>
              <strong>Legitimate interests</strong> — Art. 6(1)(f) — for
              cookieless aggregate analytics, security and fraud prevention,
              and site improvement, where those interests are not overridden
              by your rights.
            </li>
            <li>
              <strong>Legal obligation</strong> — Art. 6(1)(c) — where
              processing is required by law (record-keeping for contest,
              tax reporting for prize winners, response to lawful requests).
            </li>
          </ul>
          <p>
            Contest participation is restricted to U.S. residents. If you
            are outside the U.S. you may browse the site but are not eligible
            to enter.
          </p>
        </Section>

        <Section id="who-we-share-with" title="5. Disclosure of Personal Information">
          <p>
            We may share your personal information with the following third
            parties (These third parties listed may have privacy practices that
            differ from those mentioned in this Notice. Refer to their
            respective privacy notices and policies for more information):
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              <strong>State Farm</strong> — contest participation data,
              marketing-consent status, and (for winners) information needed
              for prize fulfillment and tax reporting. State Farm is a
              controller for these purposes.
            </li>
            <li>
              <strong>Amazon Web Services</strong> — hosting, content delivery
              (CloudFront), and transactional/marketing email delivery (SES)
              in the United States.
            </li>
            <li>
              <strong>Google (Google Tag Manager and Google Analytics)</strong>{' '}
              — gated by region: active by default in the United States and
              other non-GDPR regions (opt out any time via Privacy Settings or
              Global Privacy Control) and only after opt-in for the EEA, UK,
              and Switzerland. Tags may set cookies and send aggregated usage
              data to Google in the United States.
            </li>
            <li>
              <strong>Plausible Analytics</strong> — self-hosted on our own
              infrastructure under a first-party hostname. Plausible does not
              set cookies and aggregates visits using a daily-salted hash of
              IP address and user agent. It runs by default and you can opt
              out from the{' '}
              <a href="/privacy-settings" className="underline">
                Privacy Settings
              </a>{' '}
              page.
            </li>
            <li>
              <strong>DEEL (State Farm's marketing measurement tag)</strong> —
              loaded <strong>only for visitors detected as being in the United
              States</strong>. Visitors outside the U.S. do not have the DEEL
              script loaded at all, regardless of consent. U.S. visitors can
              opt out at any time on the Privacy Settings page, through the
              "Do Not Sell or Share" link in our footer, or by sending a
              Global Privacy Control signal from their browser. May set
              cookies and send measurement data to State Farm.
            </li>
            <li>
              <strong>Law enforcement, courts, and regulators</strong> — only
              in response to a valid legal request or where we have a good-faith
              belief disclosure is required.
            </li>
          </ul>
          <p>
            We do not sell your personal information.
          </p>
        </Section>

        <Section id="international-transfers" title="6. International transfers">
          <p>
            We host the site in the United States. If you access the site from
            outside the United States, your information will be transferred
            to, processed in, and stored in the U.S. Where required (for
            transfers from the EEA, UK, or Switzerland), the transfer is
            covered by the European Commission's Standard Contractual Clauses
            or another mechanism recognized as providing an adequate level of
            protection.
          </p>
        </Section>

        <Section id="how-long-we-keep-it" title="7. Retention of Personal Information">
          <p>
            We retain personal information only for as long as needed for the
            purposes described in this Notice and to meet legal, audit, and
            security obligations. Indicative retention periods:
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              <strong>Account data</strong> — for the life of your account
              plus a grace period after you request deletion (typically 30
              days), then erased. Audit records are retained for up to 7 years
              to support contest compliance and fraud disputes.
            </li>
            <li>
              <strong>Contest participation records</strong> — at least
              the duration of the contest season plus the statutory window
              for prize-claim disputes; longer where required for tax or
              regulatory reporting.
            </li>
            <li>
              <strong>Game scores</strong> — retained as part of the
              leaderboard archive. When an account is deleted we anonymize
              its scores rather than remove them, to preserve the integrity
              of weekly leaderboards.
            </li>
            <li>
              <strong>Cookie-consent records</strong> — up to 7 years to
              evidence consent decisions in the event of audit or complaint.
            </li>
            <li>
              <strong>Operational logs (security, errors, email delivery
              events)</strong> — typically 90 days; access logs may be retained
              longer for security investigations.
            </li>
            <li>
              <strong>One-time passcodes</strong> — discarded within 24 hours
              of issue or immediately upon use.
            </li>
          </ul>
        </Section>

        <Section id="your-rights" title="8. Individual rights">
          <p>
            Depending on where you live, you may have some or all of the
            following rights regarding your personal information:
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              <strong>Access</strong> — receive a copy of the personal
              information we hold about you.
            </li>
            <li>
              <strong>Rectification</strong> — correct inaccurate or incomplete
              information.
            </li>
            <li>
              <strong>Erasure</strong> — request deletion of your account and
              associated information, subject to legal and audit retention.
            </li>
            <li>
              <strong>Restriction and objection</strong> — limit how we process
              your information, including objection to processing based on
              legitimate interests.
            </li>
            <li>
              <strong>Portability</strong> — receive your information in a
              structured, commonly used, machine-readable format.
            </li>
            <li>
              <strong>Withdraw consent</strong> — at any time, for any
              processing based on your consent. Withdrawal does not affect the
              lawfulness of processing carried out before withdrawal.
            </li>
            <li>
              <strong>Designate</strong> — an authorized agent.
            </li>
            <li>
              <strong>Appeal</strong> — a decision involving your rights.
            </li>
            <li>
              <strong>Complain</strong> — to a supervisory authority.
            </li>
          </ul>
          <p>
            If you believe an automated decision was made about you and would
            like a human to review it, contact us at the address in Section 9.
          </p>
        </Section>

        <Section id="how-to-exercise-rights" title="9. How to exercise your rights">
          <p>
            Email us at{' '}
            <a
              href={`mailto:${PRIVACY_CONTACT}`}
              className="text-brand-redAccent underline-offset-2 hover:underline"
            >
              {PRIVACY_CONTACT}
            </a>
            . Tell us which right you want to exercise and provide enough
            information for us to find your account (typically the email
            address you registered with). We aim to respond within 30 days,
            as required by GDPR Article 12(3). For requests that are complex
            or numerous we may extend by up to 60 additional days and will
            tell you why.
          </p>
          <p>
            You can also adjust the most common settings yourself:
          </p>
          <ul className="ml-6 list-disc space-y-2">
            <li>
              Cookie and tracking preferences:{' '}
              <a href="/privacy-settings" className="underline">
                Privacy Settings
              </a>
            </li>
            <li>
              Email marketing: every marketing email contains a one-click
              unsubscribe link (per RFC 8058). You can also turn off marketing
              in your account preferences once registered.
            </li>
            <li>
              Account deletion: from your account page (when registered) or by
              email to the address above.
            </li>
          </ul>
          <p>
            We do not discriminate against you for exercising your rights.
          </p>
        </Section>

        <Section id="age-requirements" title="10. Age requirements">
          <p>
            Contest eligibility and the minimum age to enter are set by the{' '}
            <a href="/rules" className="text-brand-redAccent underline-offset-2 hover:underline">
              Official Rules
            </a>{' '}
            and are not restated here. This site is not directed to children
            under 13 of age, and we do not knowingly collect personal
            information from children under 13. If you believe you have provided
            us personal information related to someone under 13, contact us
            immediately at the address in Section 9 and we will delete it.
          </p>
        </Section>

        <Section id="contact" title="11. Contact us">
          <p>
            Operator and primary contact:
          </p>
          <p className="pl-4 text-white/75">
            Infinity Marketing Team, LLC
            <br />
            Attention: Privacy
            <br />
            <a
              href={`mailto:${PRIVACY_CONTACT}`}
              className="text-brand-redAccent underline-offset-2 hover:underline"
            >
              {PRIVACY_CONTACT}
            </a>
          </p>
        </Section>

        <Section id="changes" title="12. Changes to this notice">
          <p>
            We may update this notice from time to time. When we make
            material changes we will update the version number and the
            "Last updated" date at the top of this page and, where
            appropriate, ask you to review the changes before continuing to
            use the site. The version of this notice you saw when you made
            a privacy choice is recorded with that choice.
          </p>
        </Section>
      </div>
    </article>
  );
}

/**
 * Lead intro shown at the top of the Privacy Notice. Establishes the Official
 * Rules as the controlling document for the contest and defines the
 * "Notice" term. Wording is the legal-reviewed 2026-06-26 copy.
 */
function RulesAuthorityNotice() {
  return (
    <div
      role="note"
      className="mt-8 rounded-md border-l-4 border-brand-redAccent bg-white/[0.04] px-5 py-4 text-[15px] leading-relaxed text-white/80"
    >
      The{' '}
      <a href="/rules" className="text-brand-redAccent underline-offset-2 hover:underline">
        Official Rules
      </a>{' '}
      are the primary authority for the State Farm Gamerhood Season 5
      contest. If there is any conflict between the Official Rules and this
      Privacy Notice ("Notice") regarding the Contest, the Official Rules
      will prevail.
    </div>
  );
}

type SectionProps = {
  id: string;
  title: string;
  children: React.ReactNode;
};

function Section({ id, title, children }: SectionProps) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="font-heading text-lg uppercase tracking-wider text-white">
        {title}
      </h2>
      <div className="mt-4 space-y-4 text-[15px] text-white/80">{children}</div>
    </section>
  );
}
